import React, { useEffect, useState } from 'react'

import { Replay } from '@mui/icons-material'
import { Box, CircularProgress, Grid, IconButton, Paper, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { useInfoMessage } from 'hooks/useInfoMessage'
import _ from 'lodash'
import { convertFromDateTime } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'

import ActionButton from '../Buttons/ActionButton'
import MessageInfo from '../MessageInfo/MessageInfo'
import SelectCenter from '../SelectCenter/SelectCenter'

import type { ResponseResult } from 'api-client'

interface OptimizeRequestInterface {
  adhockType: string
}

function OptimizeRequest({ adhockType }: OptimizeRequestInterface) {
  const { loginUserInfo } = useAuthContext()
  const { infomessage, fetchInfomessage, isLoading: infomessageIsLoading } = useInfoMessage()

  const [centerIsValid, setCenterIsValid] = useState(true)
  const [centerId, setCenterId] = useState<number | null | undefined>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const isCompanyScope = centerId == null
  const isBatchProcessing = (loginUserInfo?.sysCurrentStatus ?? []).some((status) => {
    if (status.processStatus !== 'processing') return false
    if (isCompanyScope) return status.centerId == null
    return status.centerId === centerId
  })

  const onInfomessage = () => {
    fetchInfomessage({ companyId: loginUserInfo?.companyId, target: adhockType, limit: 10 })
  }

  // info_type="last_update"に該当する、一番新しい日付のinfo_timeを表示
  const lastUpdate = () => {
    const lastUpdateArray = _.filter(infomessage.list, (value) => value.infoType === 'last_update')
    return convertFromDateTime(_.first(lastUpdateArray)?.infoTime)
  }

  const execRequest = async () => {
    setIsLoading(true)
    const api = await getAPI()
    if (!api) return

    try {
      const response = await api.execOptimizeExecOptimizeGet({
        companyId: loginUserInfo?.companyId,
        centerId: centerId || undefined,
        targetType: adhockType
      })

      if (response.result.status === 'error') {
        setResultMessage(response.result)
      }
    } catch (e) {
      setResultMessage({
        status: 'error',
        message: '発注推奨数算出処理実行でエラーが発生しました。',
        pageNo: 0,
        systemDate: null,
        systemInfo: null
      })
    } finally {
      setIsLoading(false)
      onInfomessage()
    }
  }

  useEffect(() => {
    onInfomessage()
  }, [])

  return (
    <Paper variant="outlined" data-control-id={`${adhockType}-section`}>
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Typography color="royalblue">
          最終更新日:
          {infomessage.list.length > 0 && lastUpdate()}
        </Typography>
        {/* 更新ボタン */}
        <IconButton onClick={onInfomessage} data-control-id={`${adhockType}-refresh`}>
          <Replay />
        </IconButton>
      </Box>
      <Typography component="div" variant="mediumBold">
      {adhockType === 'exec_predict_optimize' ? '出荷予測＋発注推奨数算出処理' : '発注推奨数算出処理実行'}
      </Typography>
      <Grid container alignItems="flex-end">
        <Box data-control-id={`${adhockType}-select-center`}>
          <SelectCenter
            trCompanyId={loginUserInfo?.companyId}
            centerId={centerId}
            setCenterId={setCenterId}
            setCenterInfo={() => {}}
            isRequired={false}
            setIsValid={setCenterIsValid}
            dropdownListControlId={`${adhockType}-select-center-dropdown`}
          />
        </Box>
        <Box sx={{ marginBottom: '4px' }}>
          <ActionButton
            onClick={execRequest}
            caption="発注推奨数算出"
            isLoading={isLoading}
            disabled={isBatchProcessing}
            dataControlId={`${adhockType}-button-call-batch`}
          />
        </Box>
        <MessageInfo result={resultMessage} />
      </Grid>
      <Typography component="div" variant="medium" sx={{ marginTop: 1 }}>
        操作履歴
      </Typography>
      {infomessageIsLoading ? (
        <CircularProgress size={40} color="primary" />
      ) : (
        <>
          {/* 3件表示する */}
          {_.take(
            _.filter(infomessage.list, (value) => value.infoType !== 'last_update'),
            3
          ).map((value, key) => {
            let color
            if (value.infoType === 'error') {
              color = 'firebrick'
            } else if (value.infoType === 'warning') {
              color = 'darkorange'
            } else {
              color = 'black'
            }

            return (
              // eslint-disable-next-line react/no-array-index-key
              <Typography key={key} color={color} sx={{ marginTop: 1 }} data-control-id={`${adhockType}-logmessage-${key + 1}`}>
                {value.message
                  ? `${convertFromDateTime(value.infoTime)} ${value.message}`
                  : `${convertFromDateTime(value.infoTime)} ${value.infoTypeName}`}
              </Typography>
            )
          })}
        </>
      )}
    </Paper>
  )
}

export default OptimizeRequest
